<template>
    <BasePanel headline="lang_PUBLISHING_USAGE_CAPTION_SETTINGS_PANEL_EPISODES" :alvqa="alvqa">
        <template v-slot:panelContent>
            <DeletionDialog context="UsageEpisodePattern" />
            <UsageEpisodePatternDialog />
            <v-container fluid>
                <v-row class="pr-3">
                    <v-col cols="9" :alvqa="alvqa + '-description'">{{ $t('lang_PUBLISHING_USAGE_CAPTION_SETTINGS_PATTERNS_DESCRIPTION') }}</v-col>
                    <v-col cols="3" class="d-flex justify-end align-center">
                        <BaseButton
                            label="lang_GENERAL_CAPTION_NEW_ENTRY"
                            icon="add"
                            color="buttonNewEntity"
                            :alvqa="alvqa + '-button-new'"
                            @click="$store.dispatch('openUsageEpisodePatternDialog', null)"
                        />
                    </v-col>
                </v-row>

<!-- The account's own patterns, then ALV's: the order they are tried -->
                <v-row>
                    <v-col>
                        <v-data-table
                            :headers="headers"
                            :items="rows"
                            item-key="key"
                            hide-default-footer
                            :items-per-page="-1"
                            disable-sort
                            dense
                            :mobile-breakpoint="0"
                            class="usage-nested-table usage-patterns-table"
                            style="background-color: transparent"
                            :no-data-text="$t('lang_GENERAL_MESSAGE_NO_RECORDS_FOUND')"
                            :alvqa="alvqa + '-table'"
                        >
                            <template v-slot:item="{ item }">
                                <tr v-if="item.divider" :key="item.key">
                                    <td :colspan="headers.length" class="subtitle-2 pt-4" :alvqa="alvqa + '-system-caption'">
                                        {{ $t('lang_PUBLISHING_USAGE_CAPTION_SETTINGS_PATTERNS_SYSTEM') }}
                                    </td>
                                </tr>
                                <tr v-else :key="item.key" :class="{ 'subtitleGray--text': item.isSystem || !item.isActive }" :alvqa="alvqa + '-row-' + item.key">
                                    <td>{{ item.priority }}</td>
                                    <td><code class="usage-pattern">{{ item.pattern }}</code></td>
                                    <td>{{ item.example }}</td>
                                    <td>{{ $t(numberingText(item.numbering)) }}</td>
                                    <td>{{ item.dataType ? $t(dataTypeText(item.dataType)) : $t('lang_GENERIC_CAPTION_ALL') }}</td>
                                    <td class="ellipsis">{{ item.description }}</td>
                                    <td>
                                        <v-simple-checkbox
                                            :value="item.isActive"
                                            :disabled="item.isSystem"
                                            color="generalCheckbox"
                                            :alvqa="alvqa + '-row-' + item.key + '-checkbox-active'"
                                            @input="$store.dispatch('toggleUsageEpisodePattern', item)"
                                        />
                                    </td>
                                    <td class="text-right text-no-wrap">
                                        <template v-if="item.isSystem">
                                            <BaseIcon icon="lock" color="iconDelete" tooltipText="lang_GENERAL_CAPTION_ICON_NOT_EDITABLE" :alvqa="alvqa + '-row-' + item.key + '-icon-locked'" />
                                        </template>
                                        <template v-else>
                                            <BaseIcon
                                                icon="edit"
                                                tooltipText="lang_GENERAL_CAPTION_ICON_EDIT"
                                                :alvqa="alvqa + '-row-' + item.key + '-icon-edit'"
                                                @click="$store.dispatch('openUsageEpisodePatternDialog', item)"
                                            />
                                            <BaseIcon
                                                icon="delete"
                                                color="iconDelete"
                                                tooltipText="lang_GENERIC_CAPTION_DELETE"
                                                :alvqa="alvqa + '-row-' + item.key + '-icon-delete'"
                                                @click="confirmDelete(item)"
                                            />
                                        </template>
                                    </td>
                                </tr>
                            </template>
                        </v-data-table>
                    </v-col>
                </v-row>

<!-- Test a value against all patterns, as an import would -->
                <v-row>
                    <v-col cols="3" class="pt-6" :alvqa="alvqa + '-test-topic'">{{ $t('lang_PUBLISHING_USAGE_CAPTION_SETTINGS_PATTERNS_TEST') }}</v-col>
                    <v-col>
                        <v-row class="pr-3 pt-3">
                            <div :alvqa="alvqa + '-test-description'">{{ $t('lang_PUBLISHING_USAGE_CAPTION_SETTINGS_PATTERNS_TEST_DESCRIPTION') }}</div>
                        </v-row>
                        <v-row>
                            <v-col cols="4">
                                <v-text-field
                                    v-model="testValue"
                                    :label="$t('lang_PUBLISHING_SHEETS_DROPDOWN_AVTYPE_EPISODE')"
                                    placeholder="S1E64"
                                    clearable
                                    :alvqa="alvqa + '-test-textfield'"
                                />
                            </v-col>
                            <v-col cols="3">
                                <v-select
                                    v-model="testDataType"
                                    :items="dataTypeItems"
                                    :label="$t('lang_PUBLISHING_USAGE_CAPTION_DATA_TYPE')"
                                    :alvqa="alvqa + '-test-dropdown-data-type'"
                                />
                            </v-col>
                            <v-col class="d-flex align-center" :alvqa="alvqa + '-test-result'">
                                <template v-if="testResult && testResult.matched">
                                    <v-icon small color="buttonNewEntity" class="mr-2">check_circle</v-icon>
                                    <span>
                                        {{ $t('lang_PUBLISHING_USAGE_CAPTION_SETTINGS_PATTERNS_TEST_RESULT', {
                                            season: testResult.season === null ? '–' : testResult.season,
                                            episode: testResult.episode,
                                            numbering: $t(numberingText(testResult.numbering))
                                        }) }}
                                        <br>
                                        <span class="caption subtitleGray--text">
                                            {{ testResult.pattern.isSystem ? $t('lang_PUBLISHING_USAGE_CAPTION_SETTINGS_PATTERNS_SYSTEM') : $t('lang_PUBLISHING_USAGE_CAPTION_SETTINGS_PATTERNS_OWN') }}:
                                            <code class="usage-pattern">{{ testResult.pattern.pattern }}</code>
                                        </span>
                                    </span>
                                </template>
                                <template v-else-if="testResult">
                                    <v-icon small color="error" class="mr-2">help_outline</v-icon>
                                    {{ $t('lang_PUBLISHING_USAGE_CAPTION_SETTINGS_PATTERNS_TEST_NO_MATCH') }}
                                </template>
                            </v-col>
                        </v-row>
                    </v-col>
                </v-row>
            </v-container>
        </template>
    </BasePanel>
</template>

<script>
import BasePanel from '@/components/_base/BasePanel'
import BaseButton from '@/components/_buttons/BaseButton'
import BaseIcon from '@/components/_buttons/BaseIcon'
import DeletionDialog from '@/components/_dialogs/DeletionDialog'
import UsageEpisodePatternDialog from '../_dialogs/UsageEpisodePatternDialog'
import usageApi from '@/api/usageApi'
import { DATA_TYPES, NUMBERINGS, optionText } from '@/pages/2_publishing/8_usage/usageOptions'
import '@/pages/2_publishing/8_usage/usage.css'

/** The account's own episode patterns: tried before ALV's, by priority; the first that matches wins. Each change is
 *  saved at once and applies to the next import (an imported file is re-read from its row in Usage). */
export default {
    name: 'UsageEpisodePatternsSettingsPanel',
    components: { BasePanel, BaseButton, BaseIcon, DeletionDialog, UsageEpisodePatternDialog },
    data() {
        return {
            alvqa: 'account-settings-usage-episodes',
            testValue: '',
            testDataType: 'usage_statement',
            testResult: null,
            timer: null
        }
    },
    computed: {
        patterns() { return this.$store.getters.getUsageEpisodePatterns },
        rows() {
            const own = this.patterns.custom.map(p => ({ ...p, key: 'own-' + p.id }))
            const system = this.patterns.system.map((p, i) => ({ ...p, key: 'system-' + i }))
            return [...own, { key: 'divider', divider: true }, ...system]
        },
        dataTypeItems() { return DATA_TYPES.map(o => ({ value: o.value, text: this.$t(o.text) })) },
        headers() {
            const h = (text, width) => ({ text: this.$t(text), value: text, width, sortable: false })
            return [
                h('lang_PUBLISHING_USAGE_CAPTION_SETTINGS_PATTERNS_PRIORITY', '80px'),
                h('lang_PUBLISHING_USAGE_CAPTION_SETTINGS_PATTERNS_PATTERN'),
                h('lang_PUBLISHING_USAGE_CAPTION_SETTINGS_PATTERNS_EXAMPLE', '170px'),
                h('lang_PUBLISHING_USAGE_CAPTION_SETTINGS_PATTERNS_NUMBERING', '150px'),
                h('lang_PUBLISHING_USAGE_CAPTION_DATA_TYPE', '150px'),
                h('lang_GENERIC_CAPTION_DESCRIPTION', '200px'),
                h('lang_GENERIC_CAPTION_STATUS', '70px'),
                { text: '', value: 'actions', width: '80px', sortable: false }
            ]
        }
    },
    watch: {
        testValue() { this.scheduleTest() },
        testDataType() { this.scheduleTest() },
        // A changed pattern list changes the answer
        patterns() { this.scheduleTest() }
    },
    methods: {
        numberingText(value) { return optionText(NUMBERINGS, value) },
        dataTypeText(value) { return optionText(DATA_TYPES, value) },
        confirmDelete(pattern) {
            this.$store.commit('setUsageEpisodePattern', pattern)
            this.$store.commit('setShowDeletionDialog', true)
        },
        scheduleTest() {
            clearTimeout(this.timer)
            if (!this.testValue || !this.testValue.trim()) {
                this.testResult = null
                return
            }
            this.timer = setTimeout(async () => {
                const result = await usageApi.testEpisodePattern({ value: this.testValue, dataType: this.testDataType })
                this.testResult = result.ok ? result.data : null
            }, 300)
        }
    },
    beforeDestroy() {
        clearTimeout(this.timer)
    }
}
</script>

<style scoped>
/* Narrow screens scroll the table sideways instead of squeezing the pattern column */
.usage-patterns-table ::v-deep table {
    min-width: 1100px !important;
    table-layout: fixed !important;
}
.usage-pattern {
    background-color: transparent;
    font-size: 0.8rem;
    word-break: break-all;
}
</style>
